import { mapState } from 'vuex';

import SchemaFormModal from '@/components/modals/SchemaFormModal';
import { EntityType } from '@/enums/entityType.enum';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { getPath } from '@/utils/getPath';

import { notificationsMixin } from './notifications';

/**
 * Mixin przycisków
 *
 * @export
 * @param {string} entityType
 * @param {Action[]} defaultActions
 * @param {() => void} refreshData
 * @returns
 */
export function buttonsMixin(entityType, defaultActions, refreshData) {
  return {
    mixins: [notificationsMixin],
    components: { SchemaFormModal },
    data() {
      return { actionData: null };
    },
    computed: {
      ...mapState({
        buttons: state =>
          state.button.list.map(button => ({
            ...button,
            permissions: ['BUTTON.USE'],
            entityType: 'button',
            entityId: button.id,
            event: button.action ? 'execute' : 'navigate'
          })),
        documentIndex: state => state.documentIndex.data
      }),

      actions() {
        const actions = [...this.buttons];
        actions.unshift(...defaultActions);
        return actions;
      }
    },
    methods: {
      // @vuese
      // Nawiguje do wskazanego przez przycisk widoku lub otwiera modal
      navigate(data) {
        if (data.action.schemaId) {
          this.openActionDataModal(data);
        } else {
          return this.redirect(data);
        }
      },

      // @vuese
      // Nawiguje do wskazanego przez przycisk widoku
      redirect(data) {
        const query = this.extractParams(data.action.actionData?.routeQuery);
        const params = this.extractParams(data.action.actionData?.routeParams);
        if (
          data.action.actionData?.requiredRouteParams?.length &&
          data.action.actionData.requiredRouteParams.some(param => params[param] === undefined)
        ) {
          return;
        }
        this.actionData = undefined;
        this.$router.push({
          name: data.action.routeName,
          params: { ...this.$route.params, ...params },
          query: {
            ...this.$route.query,
            order: undefined,
            tabId: undefined,
            previousStatuses: undefined,
            statusId: undefined,
            desc: undefined,
            ...query
          }
        });
        return true;
      },

      // @vuese
      // Mapuje parametry dynamiczne, na ich wartości
      extractParams(params) {
        if (!params) {
          return undefined;
        }
        Object.keys(params).forEach(key => {
          const param = params[key];
          if (!param || typeof param !== 'object') {
            return;
          }
          if (!this.documentIndex) {
            params[key] = undefined;
            return;
          }

          let value = getPath(this.documentIndex, param.path);
          if (param.getter) {
            value = new Function('value', param.getter)(value);
          }
          params[key] = param.cases[value] || param.default;
        });
        return params;
      },

      // @vuese
      // Wykonuje akcję przycisku
      execute(data) {
        if (data.action.schemaId) {
          this.openActionDataModal(data);
        } else {
          this.useButton(data);
        }
      },

      // @vuese
      // Otwiera modala z danymi akcji
      openActionDataModal(data) {
        this.$store
          .dispatch('schema/get', data.action.schemaId)
          .then(() =>
            this.$store
              .dispatch('setting/findPublic', {
                code: PublicSettings.SUBMIT_BUTTON_TEXT,
                entityType: EntityType.SCHEMA,
                entityId: data.action.schemaId.toString()
              })
              .then(() => (this.actionData = data))
          )
          .catch(err => this.notifyError(Errors.CANNOT_GET_DATA));
      },

      // @vuese
      // Wywołuje akcję z danymi z modala
      sendModalData(data) {
        if (this.actionData.action.action) {
          this.useButton(this.actionData, data).then(() => this.closeActionDataModal());
        } else if (this.actionData.action.routeName) {
          this.redirect(this.actionData);
        }
      },

      // @vuese
      // Zamyka modal z danymi akcji
      closeActionDataModal() {
        this.actionData = null;
      },

      // @vuese
      // Wywołuje request użycia przycisku
      useButton(actionData, data) {
        return this.$store
          .dispatch('button/use', {
            id: actionData.action.id,
            entityId: actionData.object.id.toString(),
            entityType,
            data
          })
          .then(() => {
            this.notifySuccess(Successes.ACTION_SUCCESS);
            if (actionData.action.routeName && this.redirect(actionData)) {
              return;
            }

            if (refreshData) {
              return refreshData(this.$route);
            }
          })
          .catch(err => {
            if (err.graphQLErrors?.length) {
              this.errorModal(err.graphQLErrors[0].message);
            } else {
              this.notifyError(Errors.ERROR_TRY_AGAIN_LATER);
            }
          });
      },

      // @vuese
      // Odświeża przyciski dokumentu
      afterStatusSave(data) {
        refreshData && refreshData(this.$route).catch(_err => this.notifyError(Errors.ACTIONS_CANNOT_REFRESH));
      }
    }
  };
}
